<template>
  <div class="category-explorer">
    <div class="category-explorer__categories">
      <button
        :class="previousButtonClassName"
        @click="onPreviousClick">
        <sprite
          id="app/arrow"
          class="category-explorer__button-sprite" />
      </button>
      <div
        class="category-explorer__categories-slider"
        ref="scroller"
        @scroll="onScroll">
        <div
          class="category-explorer__categories-inner"
          ref="inner">
          <div
            :key="index"
            v-for="(row, index) in sortedCategories"
            class="category-explorer__categories-row">
            <chip
              :key="category.slug"
              v-for="category in row"
              :slug="category.slug"
              :sprite="category.sprite"
              :label="category.title"
              :modifiers="chipModifiers"
              :active="activeCategory.slug === category.slug"
              @click="onChipClick"
              @click.native="onChipClickNative"
              class="category-explorer__chip"
              :data-slug="category.slug" />
          </div>
        </div>
      </div>
      <button
        :class="nextButtonClassName"
        @click="onNextClick">
        <sprite
          id="app/arrow"
          class="category-explorer__button-sprite" />
      </button>
    </div>
    <h4
      class="category-explorer__book-gallery-headline"
      v-html="$t('galleryHeadline', { category: activeCategory.title })" />
    <book-gallery
      class="category-explorer__book-gallery"
      :books="activeCategory.books"
      :are-books-linked="areBooksLinked" />
  </div>
</template>

<script>
import Chip from "/features/category-explorer/components/chip";
import Sprite from "/shared/sprite";
import BookGallery from "/shared/book-gallery";
import bemBlockModifiers from "/utils/bem-block-modifiers";
import events from "/events/index";
import smoothscroll from "smoothscroll-polyfill";
import "/wds/sprites/app/arrow.svg";
import "/wds/sprites/category";

export const messages = {
  en: { galleryHeadline: "Top titles in <mark>{category}</mark>" },
  de: { galleryHeadline: "Top Titel aus der Kategorie <mark>{category}</mark>" }
};

export default {
  name: "CategoryExplorer",
  components: {
    Chip,
    Sprite,
    BookGallery
  },
  props: {
    categories: {
      type: Array,
      required: true
    },
    defaultCategorySlug: {
      type: String,
      default: null
    },
    areBooksLinked: {
      type: Boolean,
      default: false
    },
    chipModifiers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeCategorySlug: this.categories.find(el => el.slug === this.defaultCategorySlug)
        ? this.defaultCategorySlug
        : this.categories[0].slug,
      isPreviousActive: false,
      isNextActive: true
    };
  },
  computed: {
    sortedCategories() {
      const categories = moveToStart(this.categories, this.defaultCategorySlug);
      return chunkArray(categories, 3);
    },
    previousButtonClassName() {
      const modifiers = ["previous"];
      if (this.isPreviousActive) {
        modifiers.push("active");
      }
      return bemBlockModifiers("category-explorer__button", modifiers);
    },
    nextButtonClassName() {
      const modifiers = ["next"];
      if (this.isNextActive) {
        modifiers.push("active");
      }
      return bemBlockModifiers("category-explorer__button", modifiers);
    },
    activeCategory() {
      return this.categories.find(el => el.slug === this.activeCategorySlug);
    }
  },
  watch: {
    scrollPosition(position) {
      this.scrollTo(position);
    }
  },
  mounted() {
    smoothscroll.polyfill();
  },
  methods: {
    onChipClickNative(e) {
      this.scrollToChip(e.target.closest(".category-explorer__chip"));
    },

    onChipClick(slug) {
      this.activeCategorySlug = slug;
      events.track("body-20190913-category_explorer_chip", "1-click", slug);
    },

    onPreviousClick() {
      this.scrollToPrevious();
      const param = this.isPreviousActive ? "previous" : "previous-disabled";
      events.track("body-20190913-category_explorer_button", "1-click", param);
    },

    onNextClick() {
      this.scrollToNext();
      const param = this.isNextActive ? "next" : "next-disabled";
      events.track("body-20190913-category_explorer_button", "1-click", param);
    },

    scrollToNext() {
      const viewWidth = this.$refs.scroller.offsetWidth;
      this.scrollBy(viewWidth * 0.5);
    },

    scrollToPrevious() {
      const viewWidth = this.$refs.scroller.offsetWidth;
      this.scrollBy(- viewWidth * 0.5);
    },

    scrollToChip(el) {
      const viewWidth = this.$refs.scroller.offsetWidth;
      const destination = el.offsetLeft - viewWidth / 2 + el.offsetWidth / 2;
      this.scrollTo(destination);
    },

    scrollTo(x) {
      this.$refs.scroller.scroll({
        "behavior": "smooth",
        "left": x,
        "top": 0
      });
    },

    scrollBy(x) {
      this.$refs.scroller.scrollBy({
        "behavior": "smooth",
        "left": x,
        "top": 0
      });
    },

    onScroll() {
      const viewWidth = this.$refs.scroller.offsetWidth;
      const innerWidth = this.$refs.inner.offsetWidth;
      const position = this.$refs.scroller.scrollLeft;
      // Max is adjusted with a couple of pixels to make sure it's reached
      const max = innerWidth - viewWidth - 2;
      this.isPreviousActive = position > 0;
      this.isNextActive = position <= max;
    }
  },

  i18n: { messages }
};

const chunkArray = (array, size) => {
  const rowLength = Math.abs(array.length / 3);
  let chunked = [];
  for (let i = 0; i < size; i ++) {
    const index = i * rowLength;
    chunked.push(array.slice(index, index + rowLength));
  }
  return chunked;
};

const moveToStart = (array, slug) => {
  const arrayClone = array.slice(0);
  let index;
  arrayClone.find((el, i) => {
    if (el.slug === slug) {
      index = i;
      return true;
    }
  });
  if (index) {
    arrayClone.unshift(arrayClone.splice(index, 1)[0]);
  }
  return arrayClone;
};
</script>

<style lang="scss">
.category-explorer {
  $root: &;

  &__categories {
    display: flex;
    margin-right: - container-padding(xs);
    margin-bottom: $basex4;
    margin-left: - container-padding(xs);
    overflow: hidden;
  }

  &__categories-slider {
    margin-top: $base;
    margin-bottom: $base;
    overflow: auto;

    -webkit-overflow-scrolling: touch;

    @include media(min-width, m) {
      width: 100%;
      margin-right: 0;
      margin-left: 0;

      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  &__categories-inner {
    position: relative;

    display: inline-flex;
    flex-direction: column;
    padding-right: container-padding(xs);
    padding-left: container-padding(xs);

    @include media(min-width, m) {
      padding-right: 0;
      padding-left: 0;
    }
  }

  &__categories-row {
    display: inline-flex;

    &:not(:last-child) {
      margin-bottom: $base;
    }
  }

  &__chip {

    &:not(:last-child) {
      margin-right: $base;
    }
  }

  &__button {
    $button: &;

    position: relative;
    z-index: 1;

    color: $grey-light;

    &--previous {
      padding-right: $basex3;

      #{$root}__button-sprite {
        transform: rotate(90deg);
      }

      &#{$button}--active {
        border-right: 2px solid rgba($grey-light, 0.2);
      }
    }

    &--next {
      padding-left: $basex3;

      #{$root}__button-sprite {
        transform: rotate(-90deg);
      }

      &#{$button}--active {
        border-left: 2px solid rgba($grey-light, 0.2);
      }
    }

    &--active {
      color: $blue;

      cursor: pointer;
    }

    @include media(max-width, m) {
      display: none;
    }
  }

  &__book-gallery-headline {
    @include font-style(h4);

    margin-bottom: $basex2;

    /* stylelint-disable-next-line selector-nested-pattern */
    mark {
      color: $green2;
    }
  }
}
</style>
